import { toast } from 'sonner';

/**
 * Extract error message from API error response
 */
const extractErrorMessage = (error: any, fallbackMessage: string): string => {
  
  // Check for error.data.title (validation error format)
  if (error?.data?.title) {
    return error.data.title;
  }
  
  // Check for error.data.message (most common API error format)
  if (error?.data?.message) {
    return error.data.message;
  }
  
  // Check for error.message (general error format)
  if (error?.message) {
    return error.message;
  }
  
  // Check for error.data.error
  if (error?.data?.error) {
    return error.data.error;
  }
  
  // Check for error.error
  if (error?.error) {
    return error.error;
  }
  
  // Check for validation errors (array format)
  if (error?.data?.errors && Array.isArray(error.data.errors)) {
    return error.data.errors.join(', ');
  }
  
  // Return fallback message if no error message found
  return fallbackMessage;
};

/**
 * Extract success message from API response
 */
const extractSuccessMessage = (response: any, fallbackMessage: string): string => {
  // Check for response.data.message
  if (response?.data?.message) {
    return response.data.message;
  }
  
  // Check for response.message
  if (response?.message) {
    return response.message;
  }
  
  // Return fallback message if no success message found
  return fallbackMessage;
};

/**
 * Show success toast with response message or fallback
 */
export const showSuccessToast = (response?: any, fallbackMessage: string = 'Operation completed successfully') => {
  const message = response ? extractSuccessMessage(response, fallbackMessage) : fallbackMessage;
  toast.success(message);
};

/**
 * Show error toast with error message or fallback
 */
export const showErrorToast = (error?: any, fallbackMessage: string = 'An error occurred. Please try again.') => {
  const message = extractErrorMessage(error, fallbackMessage);
  toast.error(message);
};

/**
 * Show info toast
 */
export const showInfoToast = (message: string) => {
  toast.info(message);
};

/**
 * Show warning toast
 */
export const showWarningToast = (message: string) => {
  toast.warning(message);
};

/**
 * Show loading toast and return toast ID for dismissal
 */
export const showLoadingToast = (message: string = 'Loading...') => {
  return toast.loading(message);
};

/**
 * Dismiss a specific toast by ID
 */
export const dismissToast = (toastId: string | number) => {
  toast.dismiss(toastId);
};

/**
 * Handle async operation with loading, success, and error toasts
 */
export const handleAsyncToast = async <T>(
  operation: () => Promise<T>,
  options: {
    loadingMessage?: string;
    successMessage?: string;
    errorMessage?: string;
  } = {}
): Promise<T> => {
  const {
    // loadingMessage = 'Processing...',
    successMessage = 'Operation completed successfully',
    errorMessage = 'Operation failed. Please try again.'
  } = options;

  // const loadingToast = showLoadingToast(loadingMessage);

  try {
    const result = await operation();
    // dismissToast(loadingToast);
    showSuccessToast(result, successMessage);
    return result;
  } catch (error) {
    // dismissToast(loadingToast);
    showErrorToast(error, errorMessage);
    throw error;
  }
};

/**
 * Handle mutation response (for RTK Query mutations)
 */
export const handleMutationResponse = (
  result: { data?: any; error?: any },
  successMessage: string,
  errorMessage: string
) => {
  if (result.error) {
    showErrorToast(result.error, errorMessage);
    return false;
  }
  
  showSuccessToast(result.data, successMessage);
  return true;
};
